import cloneDeep from 'lodash/cloneDeep';
import React, { useRef } from 'react';
import { IFramework, useMoveEventListener } from '@/framework';
import { ITouchKitEvent } from '@/utils/event-kit';
import { getPointsBetweenAngle, twoPointToVector } from '@/utils/math-kit';
import { IImageLayerProps, ILayerProps, IPoint, LayerType } from '@/version-types';
import { getCenterPoint, rotateImage } from '../utils';

const ImageRotateChangeAction: React.FC<{
    stageScale: number;
    framework: IFramework;
}> = ({ framework }) => {
    const originLayerRef = useRef<ILayerProps | undefined>(undefined);
    const lastPointRef = useRef<IPoint>({ x: 0, y: 0 });
    const originScaleRef = useRef<number>(1);
    const lastRotateRef = useRef<number>(0);
    // 上一次和初始角度的角度差
    const lastSubRotateRef = useRef<number>(0);
    const centerPointRef = useRef<IPoint | null>(null);

    useMoveEventListener((e: ITouchKitEvent) => {
        if (e.isStart) {
            const layer = framework.operate.getActivePickLayer();
            originLayerRef.current = cloneDeep(layer as ILayerProps);
            lastPointRef.current = { x: e.startPoint.x, y: e.startPoint.y };
            originScaleRef.current = (originLayerRef.current.data as IImageLayerProps).imageTransform.scale;
            lastRotateRef.current = (originLayerRef.current.data as IImageLayerProps).imageTransform.rotate;
            return;
        }
        // 渲染层不存在，不可操作
        if (!originLayerRef.current || originLayerRef.current?.layerType !== LayerType.IMAGE_LAYER) {
            return null;
        }

        const target = e.target;
        const dataType = target.getAttribute('data-operate-type');

        if (dataType !== 'image-rotate') return;

        const parentElement = target.parentElement;
        if (!parentElement) return;

        const { data } = originLayerRef.current;
        const { imageTransform } = data as IImageLayerProps;

        if (!centerPointRef.current) {
            centerPointRef.current = getCenterPoint(parentElement);
        }

        const currentPoint = { x: e.currentPoint.x, y: e.currentPoint.y };

        // 获取从初始点的旋转角度
        let currentRotate = getPointsBetweenAngle(
            twoPointToVector(centerPointRef.current, lastPointRef.current),
            twoPointToVector(centerPointRef.current, currentPoint),
        );

        if (currentRotate < 0) {
            currentRotate = 360 + currentRotate;
        }

        const newLayer = rotateImage(
            originLayerRef.current,
            (imageTransform.rotate +
                currentRotate * (imageTransform.flipH ? -1 : 1) * (imageTransform.flipV ? -1 : 1)) %
                360,
            originScaleRef.current,
        );
        lastSubRotateRef.current = currentRotate;
        lastRotateRef.current = (newLayer.data as IImageLayerProps).imageTransform.rotate;
        (originLayerRef.current.data as IImageLayerProps).imageTransform.scale = (
            newLayer.data as IImageLayerProps
        ).imageTransform.scale;
        framework.stage.addOrUpdate(cloneDeep(newLayer));
    });
    return null;
};

export default ImageRotateChangeAction;
